import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    CanLoad,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';

import { AuthService } from '#root/core/auth/auth.service';
import {
    Observable,
    of,
    switchMap,
} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
    /**
     * Constructor
     */
    constructor(
        private readonly _authService: AuthService,
        private readonly _router: Router
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Can activate
     *
     * @param route
     * @param state
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
        return this._check(redirectUrl);
    }

    /**
     * Can activate child
     *
     * @param childRoute
     * @param state
     */
    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const stateParsed = state.url.includes('error') ? '/' : state.url;
        const redirectUrl = state.url === '/sign-out' ? '/' : stateParsed;
        return this._check(redirectUrl);
    }

    /**
     * Can load
     *
     * @param route
     * @param segments
     */
    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
        return this._check('/');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check the authenticated status
     *
     * @param redirectURL
     * @private
     */
private _check(redirectURL: string): Observable<boolean> {
    return this._authService.check().pipe(
        switchMap((authenticated) => {

            if (!authenticated) {
                const stateParsed = redirectURL.includes('error') ? '/' : redirectURL;
                this._router.navigate(['sign-in'], {
                    queryParams: { redirectURL: stateParsed }
                });
                return of(false);
            }
            const passwordChangedBySelf =
                localStorage.getItem('passwordChangedBySelf') === 'true';

            if (!passwordChangedBySelf) {
                if (redirectURL.includes('force-password-change') ||
                    redirectURL.includes('sign-out')) {
                    return of(true);
                }
                this._router.navigate(['/force-password-change']);
                return of(false);
            }

            return of(true);
        })
    );
}
}
